// Målløsning: fra kystkontakt til et grid noen kan gjøre noe med.
//
// EIERKRAVET, ORDRETT: «… og få laget målløsninger i Skade.»
//
// GEOMETRIEN ER IKKE VÅR. `polarTarget`, `latLonToMgrs` og `bearingDeg` kommer
// fra `@skade/contracts/targeting`. Å regne peiling+avstand → MGRS på nytt her
// ville gitt NJORD og SKADE to svar på samme spørsmål, og et grid som avviker
// mellom observatøren og skytteren er den verste feilen dette systemet kan
// gjøre. Grunnloven §3: én kilde til sannhet.
//
// DET DENNE FILA EIER er VALGET av hvilken observasjon løsningen skal bygges på,
// og ÆRLIGHETEN om hvor god den er. En løsning bygget på én RF-peiling uten
// avstand er ikke en målløsning — det er en retning. Den skal ikke kunne sendes
// videre som om den var et punkt.
import { latLonToMgrs, polarTarget, type LatLon } from '@skade/contracts';
import type { KopSensor, Kystkontakt, Observasjon } from '../api/typer.js';
import { SENSORART_NAVN } from '../api/typer.js';
import { formaterKlokke } from '@skade/sensorflate';

/** Hvor løsningen kommer fra. Rekkefølgen er kvalitet, best først. */
export type Loesningsgrunnlag =
  /** Radarmåling: sensorposisjon + peiling + avstand. Beste tilfelle. */
  | 'radar-polar'
  /** Fusjonen har løst posisjonen (krysspeiling / flere sensorer). */
  | 'fusjonert-punkt'
  /** Fusjonen har en posisjon, men den er PROJISERT fra én peiling. */
  | 'projisert-punkt';

export const GRUNNLAG_TEKST: Record<Loesningsgrunnlag, string> = {
  'radar-polar': 'Radarmåling — peiling og avstand fra sensor',
  'fusjonert-punkt': 'Krysspeilt/målt posisjon fra flere sensorer',
  'projisert-punkt': 'Projisert fra én peiling — avstanden er ANTATT',
};

/** Hva løsningen kan brukes til. Dette skal stå på knappen, ikke i et vedlegg. */
export type Loesningskvalitet = 'skarp' | 'foreløpig' | 'ikke-skytbar';

export const KVALITET_TEKST: Record<Loesningskvalitet, string> = {
  skarp: 'Skarp — målt avstand',
  foreløpig: 'Foreløpig — kontroller før bruk',
  'ikke-skytbar': 'IKKE skytbar — kun retning',
};

export interface Maalloesning {
  kontaktId: string;
  mgrs: string;
  punkt: LatLon;
  grunnlag: Loesningsgrunnlag;
  kvalitet: Loesningskvalitet;
  /** Feilradius i meter der vi kan anslå den. Mangler = vi vet ikke, og sier det. */
  cepM?: number;
  /**
   * Begrunnelsen som følger løsningen inn i SKADE. Skrevet for å bli LEST av en
   * annen operatør som ikke satt her: hvilke sensorer, hva de målte, når.
   */
  begrunnelse: string;
  /** Observasjonene løsningen står på. Sporbarhet, jf. grunnloven §1. */
  brukteObservasjoner: string[];
}

/** Hvorfor en kontakt IKKE kan bli en målløsning. Vises ordrett på knappen. */
export interface Avslag {
  grunn: string;
  /** Hva operatøren kan gjøre for å komme videre. Aldri en blindvei. */
  raad: string;
}

export type Loesningsforsok = { ok: true; loesning: Maalloesning } | { ok: false; avslag: Avslag };

/**
 * Beste radar-observasjon: nyeste `peiling-avstand` fra en sensor med kjent
 * posisjon. Uten sensorposisjon er en polar måling ubrukelig — den peker fra et
 * sted vi ikke kjenner — og da er den ikke «litt dårligere», den er ingenting.
 */
function bestePolare(
  kontakt: Kystkontakt,
  sensorPerId: Map<string, KopSensor>,
): { obs: Observasjon; sensor: KopSensor } | null {
  let best: { obs: Observasjon; sensor: KopSensor } | null = null;
  for (const obs of kontakt.observasjoner) {
    if (obs.maaling.slag !== 'peiling-avstand') continue;
    const sensor = sensorPerId.get(obs.sensorId);
    if (!sensor || sensor.lat === undefined || sensor.lon === undefined) continue;
    if (!best || obs.tsMs > best.obs.tsMs) best = { obs, sensor };
  }
  return best;
}

export interface LoesningValg {
  /** MGRS-presisjon i siffer per akse. 5 = 1 m, som i `latLonToMgrs`-standarden. */
  presisjon?: number;
}

/**
 * Bygg en målløsning, eller si tydelig hvorfor det ikke går.
 *
 * Rekkefølgen på grunnlagene er ikke tilfeldig — den er «hva vi faktisk har
 * MÅLT», ikke «hva som er lettest å regne på»:
 *   1. radar-polar     — avstanden er målt av en sensor med kjent posisjon
 *   2. fusjonert-punkt — flere sensorer har løst punktet
 *   3. projisert-punkt — én peiling, avstanden er antatt → foreløpig
 * Har vi ingenting av dette, blir det AVSLAG med en beskjed om hva som mangler.
 * Å produsere et grid fra en enslig peiling ved å gjette avstanden er den ene
 * tingen denne fila aldri skal gjøre.
 */
export function lagMaalloesning(
  kontakt: Kystkontakt,
  sensorer: readonly KopSensor[],
  valg: LoesningValg = {},
): Loesningsforsok {
  const presisjon = valg.presisjon ?? 5;
  const sensorPerId = new Map(sensorer.map((s) => [s.id, s]));

  const polar = bestePolare(kontakt, sensorPerId);
  if (polar && polar.obs.maaling.slag === 'peiling-avstand') {
    const m = polar.obs.maaling;
    const løst = polarTarget({
      observer: { lat: polar.sensor.lat!, lon: polar.sensor.lon! },
      bearing: m.peilingDeg,
      unit: 'deg',
      rangeM: m.avstandM,
    });
    return {
      ok: true,
      loesning: {
        kontaktId: kontakt.id,
        mgrs: løst.mgrs,
        punkt: løst.target,
        grunnlag: 'radar-polar',
        kvalitet: 'skarp',
        cepM: cepFraPolar(m.avstandM, m.usikkerhetDeg, m.usikkerhetM),
        begrunnelse: begrunnelse(kontakt, sensorer, `${polar.sensor.navn}: peiling ${Math.round(m.peilingDeg)}°, avstand ${Math.round(m.avstandM)} m kl. ${formaterKlokke(polar.obs.tsMs)}`),
        brukteObservasjoner: [polar.obs.id],
      },
    };
  }

  if (kontakt.lat !== undefined && kontakt.lon !== undefined) {
    const punkt: LatLon = { lat: kontakt.lat, lon: kontakt.lon };
    const mgrs = latLonToMgrs(punkt, presisjon);
    if (!mgrs) {
      return { ok: false, avslag: { grunn: 'Posisjonen kunne ikke skrives som MGRS.', raad: 'Kontroller at kontakten har gyldige koordinater fra broen.' } };
    }
    const løst = kontakt.posisjonLost;
    return {
      ok: true,
      loesning: {
        kontaktId: kontakt.id,
        mgrs,
        punkt,
        grunnlag: løst ? 'fusjonert-punkt' : 'projisert-punkt',
        kvalitet: løst ? 'skarp' : 'foreløpig',
        begrunnelse: begrunnelse(kontakt, sensorer, løst
          ? 'Krysspeilt/målt posisjon fra fusjonen'
          : 'PROJISERT posisjon fra én peiling — avstanden er antatt, ikke målt'),
        brukteObservasjoner: kontakt.observasjoner.map((o) => o.id),
      },
    };
  }

  // Har vi peilinger, men ingen avstand? Da er dette en RETNING, og det skal
  // sies med de ordene. Operatøren får konkret råd: én sensor mer med peiling
  // gir en krysspeiling, og da finnes det et punkt.
  const peilinger = kontakt.observasjoner.filter((o) => o.maaling.peilingDeg !== undefined);
  if (peilinger.length > 0) {
    const unikeSensorer = new Set(peilinger.map((o) => o.sensorId)).size;
    return {
      ok: false,
      avslag: {
        grunn: `Kontakten har ${peilinger.length} peiling(er) fra ${unikeSensorer} sensor(er), men ingen målt avstand og ingen løst posisjon.`,
        raad: unikeSensorer < 2
          ? 'Få en peiling fra én sensor mer (RF eller hydrofon) — to peilinger gir en krysspeiling.'
          : 'Vent på at fusjonen løser krysspeilingen, eller få radaren til å måle avstand i sektoren.',
      },
    };
  }
  return {
    ok: false,
    avslag: {
      grunn: 'Kontakten har ingen observasjoner med peiling eller posisjon.',
      raad: 'Kontroller at KOP-broen er tilkoblet og at minst én sensor måler.',
    },
  };
}

/**
 * Anslått feilradius for en polar måling. Vinkelfeilen vokser med avstanden —
 * en halv grad er 9 m på 1 km og 87 m på 10 km — og det er grunnen til at CEP
 * ikke kan være en konstant. Mangler broen usikkerhetstallene, returnerer vi
 * `undefined` framfor et gjettet tall: en oppdiktet CEP er verre enn ingen, for
 * den ser ut som en måling.
 */
export function cepFraPolar(avstandM: number, usikkerhetDeg?: number, usikkerhetM?: number): number | undefined {
  if (usikkerhetDeg === undefined && usikkerhetM === undefined) return undefined;
  const tverr = usikkerhetDeg === undefined ? 0 : avstandM * Math.tan((usikkerhetDeg * Math.PI) / 180);
  const langs = usikkerhetM ?? 0;
  return Math.round(Math.hypot(tverr, langs));
}

/**
 * Begrunnelsen som følger løsningen. Den er lang med vilje: den som mottar en
 * målløsning i SKADE satt ikke her og så ikke sensorstolpen, og «kontakt 4711»
 * uten kilder er ikke noe man kan handle på.
 */
function begrunnelse(kontakt: Kystkontakt, sensorer: readonly KopSensor[], hoved: string): string {
  const artPerId = new Map(sensorer.map((s) => [s.id, s.art]));
  const arter = [...new Set(kontakt.observasjoner.map((o) => artPerId.get(o.sensorId)).filter((a) => a !== undefined))];
  const deler = [
    `NJORD/${kontakt.postId}: ${kontakt.navn || kontakt.id}`,
    hoved,
    `Sensorarter som ser kontakten: ${arter.length ? arter.map((a) => SENSORART_NAVN[a!]).join(', ') : 'ingen'}`,
  ];
  // Øvingsmerket står FØRST i begrunnelsen, ikke sist: leser noen bare den første
  // linja, skal det være denne. MERK at en øvingskontakt likevel ikke kan sendes —
  // `ctx.send` nekter (`utgaaendeNektet`) og `kontaktTilTrack` returnerer `null`.
  // Merket her er for KOPIEN operatøren tar av begrunnelsen og leser opp.
  if (kontakt.ovelse) deler.unshift('ØVINGSDATA — IKKE EKTE DATA');
  return deler.join(' · ');
}
